import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Flame,
    Droplets,
    Siren,
    ShieldAlert,
    Wrench,
    Volume2,
    Settings,
    ClipboardCheck,
    CheckCircle2,
    ArrowUpRight,
    Workflow,
} from "lucide-react";
import { PageHero } from "./About";
import { SERVICES } from "../data/site";
import { LeadForm } from "../components/LeadForm";

const ICONS = {
    Flame,
    Droplets,
    Siren,
    ShieldAlert,
    Wrench,
    Volume2,
    Settings,
    ClipboardCheck,
    PipetteIcon: Workflow,
};

const Services = () => {
    const [active, setActive] = useState(SERVICES[0].slug);
    const service = SERVICES.find((s) => s.slug === active);
    const Icon = ICONS[service.icon] || Flame;

    return (
        <>
            <PageHero
                eyebrow="Our Services"
                title="Fire safety, engineered for every scale."
                subtitle="From hydraulic-calculated sprinkler networks to addressable detection systems and full pump room solutions — we cover all nine core service verticals."
            />

            <section className="section" data-testid="services-list-section">
                <div className="container-vil grid lg:grid-cols-[280px_1fr] gap-10">
                    {/* Side nav */}
                    <aside className="lg:sticky lg:top-32 self-start">
                        <div className="eyebrow mb-4">/ Service Categories</div>
                        <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
                            {SERVICES.map((s, i) => (
                                <button
                                    key={s.slug}
                                    onClick={() => setActive(s.slug)}
                                    className={`text-left px-4 py-3 text-sm border-l-2 transition-all whitespace-nowrap lg:whitespace-normal ${
                                        active === s.slug
                                            ? "border-[#E53935] bg-[#fff1f0] text-slate-900"
                                            : "border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400"
                                    }`}
                                    data-testid={`service-nav-${s.slug}`}
                                >
                                    <span className="font-mono text-xs text-slate-500 mr-2">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {s.title}
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Detail */}
                    <div className="min-w-0">
                        <div
                            className="glass corner-accent relative overflow-hidden"
                            data-testid={`service-detail-${service.slug}`}
                        >
                            <div className="relative h-72 md:h-96 overflow-hidden">
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="w-full h-full object-cover"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background:
                                            "linear-gradient(0deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.3) 60%, rgba(5,5,5,0.6) 100%)",
                                    }}
                                />
                                <div className="absolute bottom-6 left-6 right-6">
                                    <div className="w-14 h-14 rounded-sm flex items-center justify-center bg-[#E53935] mb-4">
                                        <Icon className="w-6 h-6 text-slate-900" />
                                    </div>
                                    <h2 className="font-display font-black !text-white text-3xl md:text-4xl">
                                        {service.title}
                                    </h2>
                                </div>
                            </div>
                            <div className="p-8 md:p-10">
                                <p className="text-slate-700 leading-relaxed text-lg">
                                    {service.short}
                                </p>

                                <h3 className="font-display font-bold text-slate-900 text-lg mt-8 mb-4">
                                    System Highlights
                                </h3>
                                <ul className="space-y-3 mb-8">
                                    {service.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex gap-3 text-slate-700"
                                        >
                                            <CheckCircle2 className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="grid sm:grid-cols-3 gap-3 mb-8">
                                    {["Site Survey", "Design & Approval", "Install · Test · AMC"].map(
                                        (step, i) => (
                                            <div
                                                key={step}
                                                className="bg-slate-50 border border-slate-200 p-4"
                                            >
                                                <div className="text-xs font-mono text-[#E53935] mb-1">
                                                    STEP {String(i + 1).padStart(2, "0")}
                                                </div>
                                                <div className="font-display font-semibold text-slate-900">
                                                    {step}
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-slate-200">
                                    <Link
                                        to="/contact"
                                        className="btn-primary"
                                        data-testid={`service-cta-quote-${service.slug}`}
                                    >
                                        Request Quotation <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                    <Link to="/contact" className="btn-secondary">
                                        Book Site Inspection
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                className="section relative overflow-hidden"
                style={{ background: "#f7f7f8" }}
                data-testid="services-quote-section"
            >
                <div className="container-vil grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <span className="eyebrow">Need a custom proposal?</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                            Tell us about your facility.
                        </h2>
                        <p className="text-slate-600 mt-6 leading-relaxed max-w-md">
                            Multi-system installation? Brownfield retrofit? AMC contract?
                            Share the scope and we will revert with a complete technical &
                            commercial proposal.
                        </p>
                    </div>
                    <LeadForm />
                </div>
            </section>
        </>
    );
};

export default Services;
